import React, { useEffect, useState, useRef } from 'react';
import {
  IonItem,
  IonLabel,
  IonInput,
  IonTextarea,
  IonSelect,
  IonSelectOption,
  IonButton,
  IonDatetime,
  IonGrid,
  IonRow,
  IonCol,
  IonList,
  IonIcon,
  IonChip,
  IonNote,
  IonHeader,
  IonTitle,
} from '@ionic/react';
import { add, close, documentOutline } from 'ionicons/icons';
import { LinkModel, LinkType, LinkDestination } from '../../../types/link';
import { FileDragDropUpload, FileDragDropUploadRef } from '../../controls/FileDragDropUpload';
import styles from '../Dashboard.module.css';
import { CategoriesService } from '../../../api';

export interface LinkFormBaseProps {
  initialData?: Partial<LinkModel>;
  initialDestination?: Partial<LinkDestination>;
  onFormDataChange: (formData: Partial<LinkModel>) => void;
  onDestinationChange: (destination: Partial<LinkDestination>) => void;
  onFileChange: (file: File | null) => void;
  isEdit?: boolean;
}

export const LinkFormBase: React.FC<LinkFormBaseProps> = ({
  initialData,
  initialDestination,
  onFormDataChange,
  onDestinationChange,
  onFileChange,
  isEdit = false,
}) => {
  const [formData, setFormData] = useState<Partial<LinkModel>>({
    name: '',
    type: LinkType.Redirect,
    description: '',
    tags: {},
    categoryId: undefined,
    password: undefined,
    expirationDate: undefined,
    ...initialData,
  });
  
  const [destination, setDestination] = useState<Partial<LinkDestination>>(initialDestination || {});
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [newTagKey, setNewTagKey] = useState('');
  const [newTagValue, setNewTagValue] = useState('');
  const [categories, setCategories] = useState<{ id: string; name: string }[]>([]);
  const fileUploadRef = useRef<FileDragDropUploadRef>(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    onFormDataChange(formData);
  }, [formData, onFormDataChange]);

  useEffect(() => {
    onDestinationChange(destination);
    
    // For redirect links, set the name to the URL if name is empty
    if (formData.type === LinkType.Redirect && destination.url && !formData.name) {
      setFormData(prev => ({ ...prev, name: destination.url }));
    }
  }, [destination, onDestinationChange, formData.type, formData.name]);

  const fetchCategories = async () => {
    try {
      const data = await CategoriesService.getCategories();
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleAddTag = () => {
    if (newTagKey && newTagValue) {
      setFormData(prev => ({
        ...prev,
        tags: {
          ...prev.tags,
          [newTagKey]: newTagValue,
        },
      }));
      setNewTagKey('');
      setNewTagValue('');
    }
  };

  const handleRemoveTag = (key: string) => {
    setFormData(prev => {
      const newTags = { ...prev.tags };
      delete newTags[key];
      return { ...prev, tags: newTags };
    });
  };

  const handleFileChange = (file: File) => {
    setSelectedFile(file);
    onFileChange(file);
    if (!formData.name) {
      setFormData(prev => ({ ...prev, name: file.name }));
    }
  };

  const handleTypeChange = (value: LinkType) => {
    setFormData(prev => ({ ...prev, type: value }));
    setDestination({});
    if (value !== LinkType.File) {
      setSelectedFile(null);
      onFileChange(null);
      if (fileUploadRef.current) {
        fileUploadRef.current.reset();
      }
    }
  };

  return (
    <>
    <IonHeader>
      {isEdit ? <IonTitle>Edit Link</IonTitle> : <IonTitle>Create Link</IonTitle>}
    </IonHeader>
      <IonList>
        <IonItem>
          <IonLabel position="stacked">Type</IonLabel>
          <IonSelect
            value={formData.type}
            onIonChange={e => handleTypeChange(e.detail.value)}
          >
            <IonSelectOption value={LinkType.Redirect}>Redirect</IonSelectOption>
            <IonSelectOption value={LinkType.File}>File</IonSelectOption>
            <IonSelectOption value={LinkType.Collection}>Collection</IonSelectOption>
          </IonSelect>
        </IonItem>

        {formData.type === LinkType.File && (
          <div className={styles.fileUploadSection}>
            <FileDragDropUpload
              ref={fileUploadRef}
              onChange={handleFileChange}
            />
            {selectedFile && (
              <IonItem lines="none" className={styles.selectedFile}>
                <IonIcon icon={documentOutline} slot="start" />
                <IonLabel>
                  <h2>{selectedFile.name}</h2>
                  <p>{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                </IonLabel>
              </IonItem>
            )}
          </div>
        )}

        {formData.type === LinkType.Redirect && (
          <IonItem>
            <IonLabel position="stacked">URL</IonLabel>
            <IonInput
              type="url"
              value={destination.url}
              onIonChange={e => {
                const url = e.detail.value!;
                setDestination(prev => ({ ...prev, url }));
                // If name is empty or matches the previous URL, update it to match the new URL
                if (!formData.name || formData.name === destination.url) {
                  setFormData(prev => ({ ...prev, name: url }));
                }
              }}
              required={formData.type === LinkType.Redirect}
            />
            <IonNote slot="helper">Enter the destination URL</IonNote>
          </IonItem>
        )}

        <IonItem>
          <IonLabel position="stacked">Name</IonLabel>
          <IonInput
            value={formData.name}
            onIonChange={e => setFormData(prev => ({ ...prev, name: e.detail.value! }))}
            required
          />
        </IonItem>

        <IonItem>
          <IonLabel position="stacked">Category</IonLabel>
          <IonSelect
            value={formData.categoryId}
            onIonChange={e => setFormData(prev => ({ ...prev, categoryId: e.detail.value }))}
          >
            <IonSelectOption value={undefined}>None</IonSelectOption>
            {categories.map(category => (
              <IonSelectOption key={category.id} value={category.id}>
                {category.name}
              </IonSelectOption>
            ))}
          </IonSelect>
        </IonItem>

        <IonItem>
          <IonLabel position="stacked">Description</IonLabel>
          <IonTextarea
            value={formData.description}
            onIonChange={e => setFormData(prev => ({ ...prev, description: e.detail.value! }))}
            rows={3}
          />
        </IonItem>

        <IonItem>
          <IonLabel position="stacked">Tags</IonLabel>
          <IonGrid>
            <IonRow>
              <IonCol size="5">
                <IonInput
                  value={newTagKey}
                  onIonChange={e => setNewTagKey(e.detail.value!)}
                  placeholder="Key"
                />
              </IonCol>
              <IonCol size="5">
                <IonInput
                  value={newTagValue}
                  onIonChange={e => setNewTagValue(e.detail.value!)}
                  placeholder="Value"
                />
              </IonCol>
              <IonCol size="2">
                <IonButton fill="clear" onClick={handleAddTag}>
                  <IonIcon icon={add} />
                </IonButton>
              </IonCol>
            </IonRow>
            <IonRow>
              <IonCol>
                <div className="ion-text-wrap">
                  {formData.tags && Object.entries(formData.tags).map(([key, value]) => (
                    <IonChip key={key} color="primary">
                      {key}: {value}
                      <IonIcon
                        icon={close}
                        onClick={() => handleRemoveTag(key)}
                      />
                    </IonChip>
                  ))}
                </div>
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonItem>

        <IonItem>
          <IonLabel position="stacked">Password Protection</IonLabel>
          <IonInput
            type="password"
            value={formData.password}
            onIonChange={e => setFormData(prev => ({ ...prev, password: e.detail.value! }))}
          />
        </IonItem>

        <IonItem>
          <IonLabel position="stacked">Expiration Date</IonLabel>
          <IonDatetime
            value={formData.expirationDate}
            onIonChange={e => setFormData(prev => ({ ...prev, expirationDate: e.detail.value as string }))}
          />
        </IonItem>
      </IonList>
    </>
  );
}; 